// src/lib/modal.js — helper simples de modal premium (substitui showModal/showModalPremium do monolito).
//
// API:
//   const m = openModal({
//     titulo: 'Solicitar férias',
//     codigo: 'RH-FER',
//     body: '<div>...</div>',
//     onSave: async () => { ... return true; },  // se retornar true, fecha. false ou throw mostra erro.
//     saveLabel: 'Enviar',
//     footInfo: 'A solicitação fica pendente.',
//   });
//   m.close();
//
// Mobile-first: 92vw até 480px max, scroll interno, ESC + clique no backdrop fecham.

let _activeAbort = null;

const CSS = `
.lmodal-backdrop {
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 9100;
  display: flex; align-items: center; justify-content: center;
  padding: 14px;
  animation: lm-fade 0.18s ease;
}
@keyframes lm-fade { from { opacity: 0; } to { opacity: 1; } }
.lmodal {
  width: 100%;
  max-width: 460px;
  max-height: 90vh;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 10px;
  display: flex; flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, .4);
  animation: lm-slide 0.2s ease-out;
}
html.light .lmodal { background: #fff; }
@keyframes lm-slide { from { transform: scale(.95); opacity: 0; } to { transform: none; opacity: 1; } }
.lmodal-head {
  padding: 16px 20px 12px;
  border-bottom: 1px solid var(--border);
  position: relative;
}
.lmodal-eyebrow {
  font-size: 10px; font-weight: 600;
  text-transform: uppercase; letter-spacing: .06em;
  color: var(--muted-foreground);
  margin-bottom: 3px;
}
.lmodal-title { font-size: 18px; font-weight: 600; margin: 0; letter-spacing: -.01em; }
.lmodal-close {
  position: absolute; top: 12px; right: 12px;
  width: 30px; height: 30px;
  border: none; background: transparent;
  font-size: 20px; cursor: pointer;
  color: var(--muted-foreground);
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
}
.lmodal-close:hover { background: rgba(128,128,140,.12); color: var(--foreground); }
.lmodal-body {
  padding: 18px 20px;
  overflow-y: auto;
  flex: 1;
}
.lmodal-foot {
  padding: 12px 20px;
  border-top: 1px solid var(--border);
  display: flex; gap: 8px; align-items: center;
}
.lmodal-foot-info {
  flex: 1;
  font-size: 11px;
  color: var(--muted-foreground);
  line-height: 1.4;
}
.lmodal-btn {
  padding: 9px 16px;
  border-radius: 9px;
  font-size: 13px; font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}
.lmodal-btn.cancel {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--foreground);
}
.lmodal-btn.cancel:hover { background: rgba(128,128,140,.10); }
.lmodal-btn.primary {
  background: var(--primary);
  color: var(--primary-foreground);
  border: 1px solid transparent;
}
.lmodal-btn.primary:hover { opacity: .9; }
.lmodal-btn:disabled { opacity: .55; cursor: wait; }

.lmodal-body .lm-fld { display: flex; flex-direction: column; margin-bottom: 12px; }
.lmodal-body .lm-fld label {
  font-size: 11px; font-weight: 600;
  color: var(--muted-foreground);
  text-transform: uppercase; letter-spacing: .04em;
  margin-bottom: 5px;
}
.lmodal-body .lm-fld label .req { color: var(--sc-bad); }
.lmodal-body .lm-fld input,
.lmodal-body .lm-fld select,
.lmodal-body .lm-fld textarea {
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
  height: 36px;
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 13px;
  background: transparent;
  color: var(--foreground);
  font-family: inherit;
  width: 100%;
  transition: border-color .12s, box-shadow .12s;
}
.lmodal-body .lm-fld textarea { height: auto; }
.lmodal-body .lm-fld input:focus,
.lmodal-body .lm-fld select:focus,
.lmodal-body .lm-fld textarea:focus {
  outline: none;
  border-color: var(--muted-foreground);
  box-shadow: 0 0 0 3px rgba(128,128,140,.15);
}
.lmodal-body .lm-fld textarea { resize: vertical; min-height: 70px; }
.lmodal-body .lm-grid2 { display: grid; grid-template-columns: minmax(0,1fr) minmax(0,1fr); gap: 10px; }
@media (max-width: 360px) { .lmodal-body .lm-grid2 { grid-template-columns: 1fr; } }
.lmodal-body .lm-hint {
  font-size: 11px;
  color: var(--muted-foreground);
  margin-top: -4px;
  margin-bottom: 10px;
  line-height: 1.4;
}
`;

let _styleInjected = false;
function _ensureStyle() {
  if (_styleInjected) return;
  const s = document.createElement('style');
  s.id = 'lmodal-style';
  s.textContent = CSS;
  document.head.appendChild(s);
  _styleInjected = true;
}

/**
 * Abre um modal. Retorna { close, root }.
 */
// Tokens shadcn vivem em .sc-scope — como o modal é portal no <body>, a raiz
// carrega o escopo, senão var(--primary)/--background não resolvem (viram preto).
const _scopeClass = () => 'sc-scope' + (document.documentElement.classList.contains('light') ? '' : ' dark');

export function openModal({ titulo, codigo, body, onSave, saveLabel = 'Salvar', cancelLabel = 'Cancelar', footInfo = '', size = 'normal' }) {
  _ensureStyle();
  if (_activeAbort) _activeAbort.abort();
  _activeAbort = new AbortController();
  const { signal } = _activeAbort;

  const backdrop = document.createElement('div');
  backdrop.className = 'lmodal-backdrop ' + _scopeClass();
  backdrop.innerHTML = `
    <div class="lmodal" role="dialog" aria-modal="true">
      <div class="lmodal-head">
        ${codigo ? `<div class="lmodal-eyebrow">${codigo}</div>` : ''}
        <h2 class="lmodal-title">${titulo || ''}</h2>
        <button type="button" class="lmodal-close" aria-label="Fechar">&times;</button>
      </div>
      <div class="lmodal-body">${body || ''}</div>
      ${onSave ? `
        <div class="lmodal-foot">
          <div class="lmodal-foot-info">${footInfo || ''}</div>
          <button type="button" class="lmodal-btn cancel">${cancelLabel}</button>
          <button type="button" class="lmodal-btn primary">${saveLabel}</button>
        </div>
      ` : ''}
    </div>
  `;
  document.body.appendChild(backdrop);

  const modal = backdrop.querySelector('.lmodal');
  const closeBtn = backdrop.querySelector('.lmodal-close');
  const cancelBtn = backdrop.querySelector('.lmodal-btn.cancel');
  const saveBtn = backdrop.querySelector('.lmodal-btn.primary');

  const close = () => {
    if (_activeAbort) { _activeAbort.abort(); _activeAbort = null; }
    backdrop.remove();
  };

  // ESC fecha
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  }, { signal });

  // Click no backdrop (fora do modal) fecha
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  }, { signal });

  closeBtn.addEventListener('click', close, { signal });
  cancelBtn?.addEventListener('click', close, { signal });

  if (saveBtn && onSave) {
    saveBtn.addEventListener('click', async () => {
      saveBtn.disabled = true;
      saveBtn.textContent = 'Aguarde…';
      try {
        const ok = await onSave({ close, root: modal });
        if (ok !== false) close();
      } catch (err) {
        console.error('[modal] onSave erro:', err);
        // Re-habilita botão pra retentar
        saveBtn.disabled = false;
        saveBtn.textContent = saveLabel;
      } finally {
        if (saveBtn.disabled) {
          saveBtn.disabled = false;
          saveBtn.textContent = saveLabel;
        }
      }
    }, { signal });
  }

  // Foco no primeiro input
  setTimeout(() => {
    const firstInput = modal.querySelector('input, select, textarea');
    firstInput?.focus();
  }, 80);

  return { close, root: modal };
}
